/**
 * Suomalainen vakiopohjainen lasku + tilisiirto (Pankkiviivakoodi v4 Code 128).
 * Auto-fit one A4 page regardless of row count.
 */
import { jsPDF } from "jspdf";
import JsBarcode from "jsbarcode";

export function generateBarcodeData(iban, totalAmount, refNum, dueDate) {
  const ibanClean = (iban || "").replace(/\s/g, "");
  const ibanNum = ibanClean.startsWith("FI") ? ibanClean.slice(2) : ibanClean;
  const ibanPadded = (ibanNum || "").padEnd(16, "0").slice(0, 16);
  const cents = Math.round((Number(totalAmount) || 0) * 100);
  const amountStr = String(cents).padStart(8, "0").slice(0, 8);
  const refPadded = String(refNum || "").padStart(20, "0").slice(0, 20);
  let dateStr = "000000";
  if (dueDate && /^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
    dateStr = dueDate.slice(2, 4) + dueDate.slice(5, 7) + dueDate.slice(8, 10);
  }
  return "4" + ibanPadded + amountStr + "000" + refPadded + dateStr;
}

export function generateReferenceNumber(num) {
  const base = String(num).padStart(9, "0");
  const weights = [7, 3, 1];
  let sum = 0;
  for (let i = base.length - 1, w = 0; i >= 0; i--, w++) {
    sum += parseInt(base[i], 10) * weights[w % 3];
  }
  const check = (10 - (sum % 10)) % 10;
  return base + check;
}

const fmtDate = (d) => (d ? d.split("-").reverse().join(".") : "");
const fmt = (n) => (Number(n) || 0).toFixed(2).replace(".", ",") + " \u20AC";

/** Render the Finnish "pankkiviivakoodi" as Code 128 onto an offscreen canvas. */
function buildBarcodeDataURL(data) {
  if (!data) return null;
  const canvas = document.createElement("canvas");
  try {
    JsBarcode(canvas, data, {
      format: "CODE128C",
      displayValue: false,
      width: 1,
      height: 50,
      margin: 0,
    });
  } catch {
    try {
      // Fallback: use plain CODE128 if CODE128C requires only digits
      JsBarcode(canvas, data, {
        format: "CODE128",
        displayValue: false,
        width: 1,
        height: 50,
        margin: 0,
      });
    } catch {
      return null;
    }
  }
  return canvas.toDataURL("image/png");
}

export function generateInvoicePDF(invoice, settings = {}) {
  const doc = new jsPDF({ format: "a4", unit: "mm" });
  const pageW = 210;
  const pageH = 297;
  const margin = 14;

  const text = (t, x, yy, opts = {}) => {
    doc.setFontSize(opts.size || 10);
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.text(String(t || ""), x, yy, { align: opts.align || "left" });
  };
  const line = (x1, y1, x2, y2, w = 0.3) => {
    doc.setLineWidth(w);
    doc.line(x1, y1, x2, y2);
  };

  const form = invoice || {};
  const items = form.items || [];

  // ---------------- Background / Logo ----------------
  if (settings.logo_url && settings.logo_position === "background") {
    try {
      doc.setGState && doc.setGState(new doc.GState({ opacity: (settings.logo_opacity || 15) / 100 }));
      const lw = (settings.logo_width || 120) / 2;
      const lh = (settings.logo_height || 60) / 2;
      doc.addImage(settings.logo_url, "PNG", (pageW - lw) / 2, (pageH - lh) / 2 - 30, lw, lh);
      doc.setGState && doc.setGState(new doc.GState({ opacity: 1 }));
    } catch { /* ignore */ }
  }
  if ((settings.logo_url || form.sender_logo_url) && settings.logo_position !== "background") {
    try {
      doc.addImage(settings.logo_url || form.sender_logo_url, "PNG", margin, 14, 28, 12);
    } catch { /* ignore */ }
  }

  // ---------------- Header block (fixed) ----------------
  let y = 16;
  const senderX = settings.logo_url && settings.logo_position !== "background" ? margin + 32 : margin;
  text(form.sender_name || settings.company_name || "", senderX, y, { bold: true, size: 11 });
  text(form.sender_address || "", senderX, y + 4, { size: 9 });
  if (form.sender_business_id) text(`Y-tunnus ${form.sender_business_id}`, senderX, y + 8, { size: 8 });
  if (form.sender_email) text(form.sender_email, senderX, y + 12, { size: 8 });
  if (form.sender_phone) text(`Puh. ${form.sender_phone}`, senderX, y + 16, { size: 8 });

  text("LASKU", pageW - margin, y, { bold: true, size: 18, align: "right" });
  const metaX = pageW - margin - 55;
  const metaXVal = pageW - margin;
  let ym = y + 5;
  const metaRow = (label, val) => {
    text(label, metaX, ym, { size: 8 });
    text(val ?? "", metaXVal, ym, { size: 9, bold: true, align: "right" });
    ym += 4;
  };
  metaRow("Päivämäärä", fmtDate(form.invoice_date));
  metaRow("Laskun numero", form.invoice_number);
  metaRow("Viitenumero", form.reference_number);
  metaRow("Maksuehdot", `${form.payment_terms || form.payment_terms_days || 14} pv netto`);
  metaRow("Eräpäivä", fmtDate(form.due_date));
  metaRow("Viivästyskorko", `${form.penalty_interest ?? 7} %`);

  // Recipient block
  y = 50;
  text(form.recipient_name || "", margin, y, { bold: true, size: 11 });
  if (form.recipient_business_id) text(`Y-tunnus ${form.recipient_business_id}`, margin, y + 4, { size: 8 });
  text(form.recipient_address || "", margin, y + 8, { size: 9 });
  if (form.recipient_email) text(form.recipient_email, margin, y + 12, { size: 8 });

  // ---------------- Items area – auto-fit ----------------
  const itemsTop = 70;
  const itemsBottom = 165; // hard ceiling so tilisiirto fits below
  let availH = itemsBottom - itemsTop;

  // Reserve ~30mm for VAT/totals beneath items
  const totalsHeight = 28;
  availH = availH - totalsHeight;
  const nRows = Math.max(1, items.length);

  // Header bar
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, itemsTop, pageW - 2 * margin, 6, "F");
  text("Nimi", margin + 1, itemsTop + 4, { bold: true, size: 8 });
  text("Määrä", pageW - margin - 75, itemsTop + 4, { bold: true, size: 8, align: "right" });
  text("à-hinta €", pageW - margin - 50, itemsTop + 4, { bold: true, size: 8, align: "right" });
  text("Alv-%", pageW - margin - 25, itemsTop + 4, { bold: true, size: 8, align: "right" });
  text("Yhteishinta €", pageW - margin, itemsTop + 4, { bold: true, size: 8, align: "right" });

  const rowHmax = 6.5;
  const rowH = Math.max(3.2, Math.min(rowHmax, (availH - 6) / nRows));
  const fontSize = rowH < 4 ? 7 : rowH < 5 ? 8 : 9;

  let ry = itemsTop + 6;
  items.forEach((item) => {
    // Subtle alt row background
    text(item.description || "", margin + 1, ry + rowH * 0.7, { size: fontSize });
    text(`${item.quantity} ${item.unit || ""}`, pageW - margin - 75, ry + rowH * 0.7, { size: fontSize, align: "right" });
    text(`${(Number(item.unit_price) || 0).toFixed(2).replace(".", ",")} €`, pageW - margin - 50, ry + rowH * 0.7, { size: fontSize, align: "right" });
    text(`${item.vat_percent} %`, pageW - margin - 25, ry + rowH * 0.7, { size: fontSize, align: "right" });
    text(fmt(item.total), pageW - margin, ry + rowH * 0.7, { size: fontSize, align: "right" });
    line(margin, ry + rowH, pageW - margin, ry + rowH, 0.1);
    ry += rowH;
  });

  // ---------------- Totals block ----------------
  const totY = itemsBottom - totalsHeight + 2;
  // VAT breakdown
  const vatGroups = {};
  items.forEach((item) => {
    const pct = Number(item.vat_percent) || 0;
    if (!vatGroups[pct]) vatGroups[pct] = { net: 0, vat: 0 };
    const net = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
    vatGroups[pct].net += net;
    vatGroups[pct].vat += net * (pct / 100);
  });
  const subtotal = Number(form.subtotal) || 0;
  const vatTotal = Number(form.vat_total) || 0;
  const totalAmount = Number(form.total_amount ?? form.total) || 0;

  let ty = totY;
  Object.entries(vatGroups)
    .sort(([a], [b]) => parseFloat(b) - parseFloat(a))
    .forEach(([pct, d]) => {
      text(`Veroerittely ALV ${pct} %`, 115, ty, { size: 7 });
      text(`${d.net.toFixed(2).replace(".", ",")} € + ALV ${d.vat.toFixed(2).replace(".", ",")} €`, pageW - margin, ty, { size: 7, align: "right" });
      ty += 4;
    });
  line(115, ty, pageW - margin, ty, 0.2);
  ty += 3.5;
  text("Arvonlisäveroton hinta €", 115, ty, { size: 8, bold: true });
  text(fmt(subtotal), pageW - margin, ty, { size: 8, bold: true, align: "right" });
  ty += 4;
  text("Arvonlisävero yhteensä €", 115, ty, { size: 8, bold: true });
  text(fmt(vatTotal), pageW - margin, ty, { size: 8, bold: true, align: "right" });
  ty += 4;
  line(115, ty, pageW - margin, ty, 0.5);
  ty += 4;
  text("Maksettava €", 115, ty, { size: 10, bold: true });
  text(fmt(totalAmount), pageW - margin, ty, { size: 11, bold: true, align: "right" });

  // Notes
  if (form.notes) {
    text(String(form.notes).slice(0, 200), margin, totY + 4, { size: 7 });
  }

  // ---------------- Tilisiirto (lower payment slip) ----------------
  const lowY = 170;
  line(margin, lowY, pageW - margin, lowY, 1);
  let ly = lowY + 5;
  // Row: Eräpäivä | Viitenumero | Yhteensä
  text("ERÄPÄIVÄ", margin, ly, { size: 7 });
  text("VIITENUMERO", margin + 50, ly, { size: 7 });
  text("YHTEENSÄ", pageW - margin, ly, { size: 7, align: "right" });
  ly += 5;
  text(fmtDate(form.due_date), margin, ly, { size: 11, bold: true });
  text(form.reference_number || "", margin + 50, ly, { size: 11, bold: true });
  text(fmt(totalAmount), pageW - margin, ly, { size: 12, bold: true, align: "right" });
  ly += 3;
  line(margin, ly, pageW - margin, ly, 0.3);

  // BIC / IBAN
  ly += 4;
  text("BIC / SWIFT", margin, ly, { size: 7 });
  text("IBAN", margin + 35, ly, { size: 7 });
  ly += 4;
  const bic = settings.bic || (form.sender_bank_account?.startsWith("FI") ? "NDEAFIHH" : "");
  text(bic, margin, ly, { size: 10, bold: true });
  text((form.sender_bank_account || settings.bank_account || ""), margin + 35, ly, { size: 10, bold: true });
  // Right side: sender info
  text(form.sender_name || settings.company_name || "", pageW - margin, ly - 4, { size: 8, bold: true, align: "right" });
  text(form.sender_address || "", pageW - margin, ly, { size: 7, align: "right" });
  ly += 4;
  line(margin, ly, pageW - margin, ly, 0.3);

  // Saaja / Mottagare
  ly += 4;
  text("Saaja / Mottagare", margin, ly, { size: 7 });
  ly += 4;
  text(form.sender_name || settings.company_name || "", margin + 30, ly, { size: 9, bold: true });
  text(form.sender_address || "", margin + 30, ly + 4, { size: 8 });
  // Signature box (right side)
  text("Allekirjoitus / Underskrift", pageW - margin - 60, ly, { size: 7 });
  line(pageW - margin - 60, ly + 5, pageW - margin, ly + 5, 0.3);
  ly += 9;
  line(margin, ly, pageW - margin, ly, 0.3);

  // Maksaja
  ly += 4;
  text("Maksajan nimi ja osoite / Betalarens namn och adress", margin, ly, { size: 7 });
  ly += 4;
  text(form.recipient_name || "", margin + 30, ly, { size: 9, bold: true });
  text(form.recipient_address || "", margin + 30, ly + 4, { size: 8 });
  // Account number field
  text("Tilitä nro / Från konto nr", pageW - margin - 60, ly, { size: 7 });
  line(pageW - margin - 60, ly + 5, pageW - margin, ly + 5, 0.3);
  ly += 9;
  line(margin, ly, pageW - margin, ly, 0.3);

  // ---------------- Barcode (Code 128) ----------------
  const barcode = form.barcode_data || generateBarcodeData(
    form.sender_bank_account || settings.bank_account,
    totalAmount,
    form.reference_number,
    form.due_date,
  );
  const barcodeImg = buildBarcodeDataURL(barcode);
  ly += 3;
  if (barcodeImg) {
    try {
      // Width 100mm, height 18mm at bottom-left
      doc.addImage(barcodeImg, "PNG", margin, ly, 110, 14);
    } catch { /* ignore */ }
  }
  // Text below barcode (monospaced)
  doc.setFontSize(7);
  doc.setFont("courier", "normal");
  doc.text(barcode, margin, ly + 17);

  // Right legal text
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  const legal = "Maksu välitetään saajalle maksujenvälityksen ehtojen mukaisesti ja vain maksajan ilmoittaman tilinumeron perusteella. Betalningen förmedlas till mottagaren enligt villkoren för betalningstjänster och endast till det kontonummer som betalaren angett.";
  const split = doc.splitTextToSize(legal, 60);
  doc.text(split, pageW - margin, ly + 2, { align: "right" });

  return doc;
}

export function downloadInvoicePDF(invoice, settings = {}) {
  const doc = generateInvoicePDF(invoice, settings);
  const filename = `lasku-${invoice.invoice_number || "luonnos"}.pdf`;
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  // 1) Try direct anchor download (works in standalone browsers)
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 0);
  } catch {
    /* ignore */
  }
  // 2) Open the PDF in a new tab as a safety net for iframe-sandboxed environments
  //    (Emergent preview, embedded views) where <a download> is silently blocked.
  try {
    const w = window.open(url, "_blank", "noopener");
    if (!w) {
      // popup blocked – inject a visible link that the user can click
      _showSavePdfFallback(url, filename);
    }
  } catch {
    _showSavePdfFallback(url, filename);
  }
  setTimeout(() => URL.revokeObjectURL(url), 90 * 1000);
}

export function printInvoicePDF(invoice, settings = {}) {
  const doc = generateInvoicePDF(invoice, settings);
  doc.autoPrint();
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  // 1) Try opening in a new tab (Chrome/Firefox will show print dialog automatically)
  let opened = null;
  try {
    opened = window.open(url, "_blank", "noopener");
  } catch {
    /* ignore */
  }
  if (!opened) {
    // 2) Fallback: hidden iframe + .print() (works in sandboxed iframes that allow scripts)
    try {
      const ifr = document.createElement("iframe");
      ifr.style.position = "fixed";
      ifr.style.right = "0";
      ifr.style.bottom = "0";
      ifr.style.width = "0";
      ifr.style.height = "0";
      ifr.style.border = "0";
      ifr.src = url;
      ifr.onload = () => {
        try {
          ifr.contentWindow && ifr.contentWindow.focus();
          ifr.contentWindow && ifr.contentWindow.print();
        } catch {
          /* ignore */
        }
        setTimeout(() => {
          if (ifr.parentNode) ifr.parentNode.removeChild(ifr);
          URL.revokeObjectURL(url);
        }, 60 * 1000);
      };
      document.body.appendChild(ifr);
    } catch {
      _showSavePdfFallback(url, `lasku-${invoice.invoice_number || "luonnos"}.pdf`);
    }
  } else {
    setTimeout(() => URL.revokeObjectURL(url), 90 * 1000);
  }
}

function _showSavePdfFallback(url, filename) {
  // Render a small modal-style banner with a direct link the user can click
  let container = document.getElementById("apps2go-pdf-fallback");
  if (container) container.parentNode.removeChild(container);
  container = document.createElement("div");
  container.id = "apps2go-pdf-fallback";
  container.style.cssText = `
    position: fixed; bottom: 24px; right: 24px; z-index: 10000;
    background: #18181b; color: #fff; padding: 16px 20px; border-radius: 12px;
    border: 1px solid #f59e0b; box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    font-family: system-ui, -apple-system, Segoe UI, sans-serif; font-size: 13px;
    max-width: 360px;
  `;
  container.innerHTML = `
    <div style="font-weight: 600; margin-bottom: 8px; color: #fbbf24;">PDF on valmis</div>
    <p style="margin: 0 0 12px 0; color: #a1a1aa;">Selain esti automaattisen latauksen. Klikkaa linkkiä avataksesi PDF.</p>
    <div style="display: flex; gap: 8px; flex-wrap: wrap;">
      <a href="${url}" target="_blank" rel="noopener"
         style="background: #f59e0b; color: #18181b; padding: 8px 14px; border-radius: 8px; text-decoration: none; font-weight: 600;">
        Avaa PDF
      </a>
      <a href="${url}" download="${filename}"
         style="background: #27272a; color: #fff; padding: 8px 14px; border-radius: 8px; text-decoration: none;">
        Lataa
      </a>
      <button id="apps2go-pdf-fallback-close"
              style="background: transparent; color: #a1a1aa; border: 0; padding: 8px; cursor: pointer;">
        Sulje
      </button>
    </div>
  `;
  document.body.appendChild(container);
  document.getElementById("apps2go-pdf-fallback-close").addEventListener("click", () => {
    if (container.parentNode) container.parentNode.removeChild(container);
  });
  setTimeout(() => {
    if (container.parentNode) container.parentNode.removeChild(container);
  }, 30 * 1000);
}

export function downloadInvoiceTXT(invoice, settings = {}) {
  const form = invoice || {};
  const items = form.items || [];
  const subtotal = Number(form.subtotal) || 0;
  const vatTotal = Number(form.vat_total) || 0;
  const totalAmount = Number(form.total_amount ?? form.total) || 0;
  const barcode = form.barcode_data || generateBarcodeData(
    form.sender_bank_account || settings.bank_account,
    totalAmount,
    form.reference_number,
    form.due_date,
  );
  const lines = [
    `LASKU #${form.invoice_number}`,
    `Päivämäärä: ${form.invoice_date}  |  Eräpäivä: ${form.due_date}`,
    `Viitenumero: ${form.reference_number}`,
    `Maksuehto: ${form.payment_terms || form.payment_terms_days || 14} pv  |  Viivästyskorko: ${form.penalty_interest ?? 7}%`,
    "",
    `LÄHETTÄJÄ:  ${form.sender_name || settings.company_name || ""}  |  Y-tunnus: ${form.sender_business_id || ""}`,
    `${form.sender_address || ""}`,
    `${form.sender_email || ""}  |  ${form.sender_phone || ""}`,
    `Tilinumero: ${form.sender_bank_account || settings.bank_account || ""}`,
    "",
    `VASTAANOTTAJA:  ${form.recipient_name}  |  Y-tunnus: ${form.recipient_business_id || ""}`,
    `${form.recipient_address || ""}`,
    `${form.recipient_email || ""}`,
    "",
    "RIVIT:",
    ...items.map(
      (it) =>
        `  ${(it.description || "").padEnd(35)} ${it.quantity} ${it.unit || ""}  á ${it.unit_price} €  ALV ${it.vat_percent}%  =  ${(Number(it.total) || 0).toFixed(2)} €`,
    ),
    "",
    `Veroton:  ${subtotal.toFixed(2)} €`,
    `ALV:      ${vatTotal.toFixed(2)} €`,
    `YHTEENSÄ: ${totalAmount.toFixed(2)} €`,
    "",
    form.notes ? `Lisätiedot: ${form.notes}` : "",
    "",
    `Viivakoodi: ${barcode}`,
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `lasku-${form.invoice_number || "luonnos"}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

export { buildBarcodeDataURL };
